import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '../common';
import { fetchAllProjectProblems, getBlockingFlags, getFlagDisplay } from './ocrApi';
import { buildScopeMaps } from './numberPlan';
import RenumberModal from './RenumberModal';

/**
 * 번호 지도 (OcrHub 문제 목록 탭 상단).
 *
 * 1..최대 번호를 칸으로 늘어놓고 겹친 번호와 빈 번호를 색으로 말한다.
 * 매칭 결과가 옳은지는 결국 "번호가 빠짐없이 한 번씩 매겨졌는가"인데, 20건씩 페이지를 넘겨 보는
 * 목록으로는 그 판정을 할 수 없다. 그래서 여기만 전량을 읽는다.
 *
 * scope(단원)별로 나눠 그린다 — numbering_mode=1(단원별 재시작) 프로젝트에서는 같은 번호가
 * 단원마다 정당하게 존재하므로, 한 덩어리로 세면 멀쩡한 번호를 겹쳤다고 말하게 된다.
 *
 * 겹친 칸을 누르면 그 번호를 미리 고른 채로 '번호 다시 매기기'가 열린다.
 */

const GRID_CAP = 500; // 이보다 큰 번호까지 칸으로 그리면 지도가 아니라 벽이 된다 — 요약으로 강등

// 칸 하나의 겉모습 — 구조 문제(겹침)가 가장 크게 보이고, 그 다음이 검수 결과다.
// 검수대기는 '아직 판단 안 함'이라 색을 주지 않는다(색이 많아지면 겹침이 묻힌다).
function cellStyle({ dup, empty, reviewStatus, blocked }) {
  if (dup) return 'border-red-400 bg-red-100 font-bold text-red-700 ring-1 ring-red-300';
  if (empty) return 'border-dashed border-gray-300 bg-gray-50 text-gray-300';
  // 차단 플래그는 검수 상태와 무관하게 표시한다 — 승인(200) 뒤에도 재-OCR 등으로 플래그가
  // 되살아나면 그 문제는 승격에서 skip된다. 초록만 보고 '승격 준비 완료'로 읽히면 안 된다.
  const ring = blocked ? ' ring-1 ring-rose-300' : '';
  if (reviewStatus === 200) return `border-green-300 bg-green-100 text-green-800${ring}`;
  if (reviewStatus === 400) return `border-amber-400 bg-amber-100 text-amber-800${ring}`;
  return blocked
    ? `border-rose-300 bg-white text-gray-600${ring}`
    : 'border-gray-200 bg-white text-gray-600';
}

function ScopeGrid({ map, onPickNumber, onOpenProblem, indexById, disabled }) {
  const { span, byNum, dupes, gaps } = map;
  if (span === 0) return <p className="text-xs text-gray-400">인덱스가 매겨진 문제가 없습니다.</p>;
  if (span > GRID_CAP) {
    return (
      <p className="text-xs text-gray-500">
        인덱스가 {span}까지 있어 칸으로 그리지 않습니다 — 겹침 {dupes.length}개
        {dupes.length > 0 && ` (${dupes.slice(0, 20).join(', ')}${dupes.length > 20 ? ' …' : ''})`}
        {' · '}빈칸 {gaps.length}개
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {Array.from({ length: span }, (_, i) => i + 1).map((n) => {
        const hit = byNum.get(n) || [];
        const dup = hit.length > 1;
        const empty = hit.length === 0;
        const one = hit.length === 1 ? hit[0] : null;
        const reviewStatus = one?.review_status ?? null;
        const blockers = one ? getBlockingFlags(one.flags) : [];
        const blocked = blockers.length > 0;
        const cls = cellStyle({ dup, empty, reviewStatus, blocked });
        // 겹친 칸은 '정리'가 먼저다(문제 둘 중 무엇을 열지도 모호하다) — 종전대로 번호 다시 매기기.
        // 한 건짜리 칸은 그 문제를 검수 모달로 연다.
        const canRenumber = dup && !disabled;
        const canOpen = !!one && !!onOpenProblem;
        const label = reviewStatus === 200 ? '검수완료' : reviewStatus === 400 ? '반려' : '검수대기';
        const title = dup
          ? `${n}번 — ${hit.length}개 겹침 (눌러서 정리)`
          : empty
            ? `${n}번 없음`
            : `${n}번 · ${label}${blocked ? ` · 승인 불가(${blockers.map((c) => getFlagDisplay(c).text).join(', ')})` : ''}`
              + (canOpen ? ' — 눌러서 열기' : '');
        return (
          <button
            key={n}
            type="button"
            onClick={canRenumber ? () => onPickNumber(n)
              : canOpen ? () => onOpenProblem(one.ocr_prob_id, indexById?.get(one.ocr_prob_id))
                : undefined}
            disabled={!canRenumber && !canOpen}
            title={title}
            className={`h-7 min-w-7 rounded-md border px-1 text-[10px] leading-none transition-colors ${cls} ${
              canRenumber ? 'cursor-pointer hover:bg-red-200'
                : canOpen ? 'cursor-pointer hover:brightness-95' : 'cursor-default'
            }`}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

function ProblemNumberMap({ projectId, reloadKey, closed = false, onChanged, onOpenProblem, getAccessToken }) {
  const [open, setOpen] = useState(true);
  const [items, setItems] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [renumberOpen, setRenumberOpen] = useState(false);
  const [pickedNumber, setPickedNumber] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchAllProjectProblems(projectId, getAccessToken);
      setItems(d.items);
      setTruncated(d.truncated);
    } catch (e) {
      setItems([]);
      setError(e.message || '인덱스 지도를 만들지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [projectId, getAccessToken]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const maps = useMemo(() => buildScopeMaps(items), [items]);
  // 목록의 페이지 계산에 쓰는 '필터 없는 전체 순서' 위치 — 지도만 전량을 읽으므로 여기서 알려준다
  const indexById = useMemo(() => {
    const m = new Map();
    items.forEach((p, i) => m.set(p.ocr_prob_id, i));
    return m;
  }, [items]);
  const totalDupes = maps.reduce((a, m) => a + m.dupes.length, 0);
  const totalGaps = maps.reduce((a, m) => a + m.gaps.length, 0);

  const openRenumber = (n = null) => {
    setPickedNumber(n);
    setRenumberOpen(true);
  };

  // 번호를 옮기면 지도와 부모 목록이 함께 낡는다 — 둘 다 다시 읽는다
  const handleApplied = () => {
    load();
    onChanged && onChanged();
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
        >
          {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          인덱스 지도
        </button>

        {!loading && !error && (
          <span className="text-xs text-gray-500">
            문제 {items.length}건
            {totalDupes > 0 ? (
              <span className="ml-1 font-medium text-red-600">· 겹친 인덱스 {totalDupes}개</span>
            ) : (
              <span className="ml-1 text-green-700">· 겹친 인덱스 없음</span>
            )}
            {totalGaps > 0 && <span className="ml-1 text-gray-400">· 빈 인덱스 {totalGaps}개</span>}
          </span>
        )}
        {loading && <span className="text-xs text-gray-400">인덱스를 읽는 중…</span>}
        {error && <span className="text-xs text-red-600">{error}</span>}

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1 rounded-full px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 disabled:opacity-40"
          >
            <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
            새로고침
          </button>
          <Button
            size="xs"
            variant={totalDupes > 0 ? 'primary' : 'primary-outline'}
            onClick={() => openRenumber(null)}
            disabled={loading || closed || items.length === 0}
          >
            인덱스 다시 매기기
          </Button>
        </div>
      </div>

      {truncated && (
        <p className="mt-2 text-xs text-amber-700">
          문제가 너무 많아 일부만 읽었습니다 — 지도에 없는 인덱스가 있을 수 있습니다.
        </p>
      )}

      {open && !loading && !error && (
        <div className="mt-3 space-y-3">
          {maps.map((m) => (
            <div key={m.scope}>
              {m.scope !== '__single__' && (
                <p className="mb-1 text-xs font-medium text-gray-700">[{m.scope}] {m.count}건</p>
              )}
              <ScopeGrid
                map={m}
                onPickNumber={openRenumber}
                onOpenProblem={onOpenProblem}
                indexById={indexById}
                disabled={closed}
              />
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-3 text-[10px] text-gray-400">
            <span className="flex items-center gap-1">
              <span className="inline-block size-3 rounded border border-red-400 bg-red-100" /> 겹침 — 눌러서 정리
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block size-3 rounded border border-dashed border-gray-300 bg-gray-50" /> 빈 인덱스
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block size-3 rounded border border-gray-200 bg-white" /> 검수대기
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block size-3 rounded border border-green-300 bg-green-100" /> 검수완료
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block size-3 rounded border border-amber-400 bg-amber-100" /> 반려
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block size-3 rounded border border-rose-300 bg-white ring-1 ring-rose-200" /> 승인 불가(차단 플래그)
            </span>
          </div>
        </div>
      )}

      <RenumberModal
        isOpen={renumberOpen}
        items={items}
        initialNumber={pickedNumber}
        onClose={() => setRenumberOpen(false)}
        onApplied={handleApplied}
        getAccessToken={getAccessToken}
      />
    </div>
  );
}

export default ProblemNumberMap;
